import { openDB } from 'idb';

const DB_NAME = 'soundbetween-audio-cache';
const DB_VERSION = 1;
const STORE_NAME = 'audio';
const MAX_CACHE_SIZE = 200 * 1024 * 1024; // 200MB

/**
 * AudioCacheService - Handles all IndexedDB operations for audio caching
 */
class AudioCacheService {
    constructor() {
        this.dbPromise = null;
    }

    /**
     * Initialize IndexedDB database
     */
    async initDB() {
        if (this.dbPromise) {
            return this.dbPromise;
        }

        try {
            this.dbPromise = openDB(DB_NAME, DB_VERSION, {
                upgrade(db) {
                    if (!db.objectStoreNames.contains(STORE_NAME)) {
                        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
                        store.createIndex('projectId', 'projectId', { unique: false });
                        store.createIndex('clipId', 'clipId', { unique: false });
                        store.createIndex('timestamp', 'timestamp', { unique: false });
                    }
                },
            });

            return this.dbPromise;
        } catch (error) {
            console.error('Failed to initialize IndexedDB:', error);
            this.dbPromise = null;
            return null;
        }
    }

    /**
     * Generate composite key for cache entries
     */
    getCacheKey(projectId, clipId) {
        return `${projectId}_${clipId}`;
    }

    /**
     * Get cached audio from IndexedDB
     * @returns {string|null} Blob URL or null if not cached
     */
    async getCachedAudio(projectId, clipId) {
        try {
            const db = await this.initDB();
            if (!db) return null;

            const key = this.getCacheKey(projectId, clipId);
            const entry = await db.get(STORE_NAME, key);

            if (entry && entry.blob) {
                return URL.createObjectURL(entry.blob);
            }

            return null;
        } catch (error) {
            console.error('Error retrieving cached audio:', error);
            return null;
        }
    }

    /**
     * Store a pre-fetched audio blob in the cache
     * @param {string} projectId
     * @param {string} clipId
     * @param {Blob} blob - Pre-fetched audio blob
     * @returns {string|null} Blob URL or null on error
     */
    async cacheAudio(projectId, clipId, blob) {
        try {
            const db = await this.initDB();
            if (!db) return null;

            // Check if we have enough space
            const cacheSize = await this.getCacheSize();
            if (cacheSize + blob.size > MAX_CACHE_SIZE) {
                await this.evictOldestAudio(3);
            }

            // Store in IndexedDB
            const key = this.getCacheKey(projectId, clipId);
            const entry = {
                key,
                projectId: String(projectId),
                clipId: String(clipId),
                blob,
                timestamp: Date.now(),
                size: blob.size,
            };

            await db.put(STORE_NAME, entry);

            // Create and return blob URL
            return URL.createObjectURL(blob);
        } catch (error) {
            if (error.name === 'QuotaExceededError') {
                console.error('Storage quota exceeded:', error);
                await this.evictOldestAudio(3);
                return this.cacheAudio(projectId, clipId, blob);
            } else {
                console.error('Error caching audio:', error);
            }
            return null;
        }
    }

    /**
     * Delete specific cached audio
     */
    async deleteCachedAudio(projectId, clipId) {
        try {
            const db = await this.initDB();
            if (!db) return;

            const key = this.getCacheKey(projectId, clipId);
            await db.delete(STORE_NAME, key);
        } catch (error) {
            console.error('Error deleting cached audio:', error);
        }
    }

    /**
     * Delete all cached audio for a project
     */
    async deleteProjectAudio(projectId) {
        try {
            const db = await this.initDB();
            if (!db) return;

            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const index = store.index('projectId');

            const keys = await index.getAllKeys(String(projectId));

            await Promise.all(keys.map(key => store.delete(key)));
            await tx.done;
        } catch (error) {
            console.error('Error deleting project audio:', error);
        }
    }

    /**
     * Clear all cached audio
     */
    async clearAllCache() {
        try {
            const db = await this.initDB();
            if (!db) return;

            const tx = db.transaction(STORE_NAME, 'readwrite');
            await tx.objectStore(STORE_NAME).clear();
            await tx.done;

        } catch (error) {
            console.error('Error clearing cache:', error);
        }
    }

    /**
     * Get total cache size
     */
    async getCacheSize() {
        try {
            const db = await this.initDB();
            if (!db) return 0;

            const entries = await db.getAll(STORE_NAME);
            return entries.reduce((total, entry) => total + (entry.size || 0), 0);
        } catch (error) {
            console.error('Error getting cache size:', error);
            return 0;
        }
    }

    /**
     * Evict oldest audio (LRU strategy)
     */
    async evictOldestAudio(count = 3) {
        try {
            const db = await this.initDB();
            if (!db) return;

            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const index = store.index('timestamp');

            // Get all entries sorted by timestamp
            const entries = await index.getAll();

            // Delete the oldest entries
            const toDelete = entries.slice(0, Math.min(count, entries.length));
            await Promise.all(toDelete.map(entry => store.delete(entry.key)));

            await tx.done;
        } catch (error) {
            console.error('Error evicting old audio:', error);
        }
    }

    /**
     * List all cached audio (for debugging)
     */
    async listAll() {
        try {
            const db = await this.initDB();
            if (!db) return [];

            const entries = await db.getAll(STORE_NAME);
            return entries.map(entry => ({
                key: entry.key,
                projectId: entry.projectId,
                clipId: entry.clipId,
                url: entry.url,
                size: entry.size,
                timestamp: new Date(entry.timestamp).toISOString(),
            }));
        } catch (error) {
            console.error('Error listing cache:', error);
            return [];
        }
    }
}

const audioCacheService = new AudioCacheService();

// Expose debug utilities in development
if (process.env.NODE_ENV === 'development') {
    window.AudioCacheDebug = {
        listAll: () => audioCacheService.listAll(),
        getStorageInfo: async () => {
            if (navigator.storage && navigator.storage.estimate) {
                return await navigator.storage.estimate();
            }
            return null;
        },
        clearCache: () => audioCacheService.clearAllCache(),
        getCacheSize: () => audioCacheService.getCacheSize(),
    };
}

export default audioCacheService;
